'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export const ReferenceCard = ({
  bigCard,
  reference,
}: {
  bigCard?: boolean;
  reference: Reference;
}) => {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const { t } = useTranslations();

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6',
          bigCard ? 'max-w-[946px]' : 'max-w-[574px]',
        )}
      >
        <div className="relative aspect-video w-full">
          <span className="absolute right-4 top-4 z-10 bg-white px-3 py-1.5 text-center font-heading text-xs uppercase text-foreground sm:px-4 sm:py-2 sm:text-sm lg:text-base">
            {reference.categories[0]?.name}
          </span>
          <Image
            onClick={handleOpenGallery(reference.images, 0)}
            src={reference?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(reference?.image, reference?.name)}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            className={cn('absolute inset-0 z-0 h-full w-full', {
              'cursor-pointer': reference.images.length > 0,
            })}
            sizes={
              bigCard
                ? '(min-width: 1024px) 946px, (min-width: 768px) 50vw, 100vw'
                : '(min-width: 1024px) 574px, (min-width: 768px) 50vw, 100vw'
            }
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-0">
          <h5
            className={cn(
              'font-heading uppercase',
              'text-lg font-bold text-foreground sm:text-xl md:text-2xl xl:text-3xl',
            )}
          >
            {reference.name}
          </h5>
          {reference.sheet !== null ? (
            <Button asChild className="mt-4 flex w-full items-center justify-center gap-2 sm:w-fit">
              <Link prefetch={false} href={reference.sheet.url} target="_blank">
                {t('text1558')} <MdKeyboardArrowRight size={20} />
              </Link>
            </Button>
          ) : (
            <HtmlRendererRaw
              className={cn('line-clamp-1 text-foreground', 'ea-reference-card__description')}
              html={reference.description}
            />
          )}
        </div>
      </div>
    </>
  );
};
